import React, { Component } from 'react'
import { Descriptions, Form, FormInstance, Select, Tag } from 'antd'
import { SizeConsumer, SizeContextData } from '../../../../context/SizeContext'
import { ShelterConsumer, ShelterContextData } from 'context/ShelterContext'
import { DEFAULT_SELECT_FILTER_OPTION } from 'helpers/form-fields.helper'
import { sbListAnimalSize } from 'services/integration'
import ShelterBuddyExternalField from '../ShelterBuddyExternalField'
import MappingStepper, { Step } from '../MappingStepper'
import { DIRECTION_LABEL, Flow, Partner } from '../shared/types'
import { resolveFlow } from '../shared/resolvers'
import { makeSbLoader } from '../shared/sbOptions'
import { ActiveField, IncomingStep, IntegrationField, ManualExternalInputs, PartnerField, ScopeField } from '../shared/fields'

// Size mappings key off the external Name (not the Id) — store it in external_id.
const loadAnimalSizes = makeSbLoader(sbListAnimalSize, 'sizes', v => v.Name)

type MappingSizeFormProps = {
  form: FormInstance
  partnerList: Partner[] | null
  /** Save-in-progress flag, forwarded to the final-step Save button. */
  loading?: boolean
}

type MappingSizeFormInnerProps = MappingSizeFormProps & {
  sizeContext: SizeContextData
  shelterContext: ShelterContextData
}

type MappingSizeFormInnerState = {
  flow: Flow
}

class MappingSizeFormInner extends Component<MappingSizeFormInnerProps, MappingSizeFormInnerState> {
  constructor(props: MappingSizeFormInnerProps) {
    super(props)
    this.state = {
      flow: resolveFlow(props.form, 'size_key'),
    }
  }

  componentDidMount() {
    const { sizeContext, shelterContext, form } = this.props

    // Initial values may settle after the Form mounts — re-resolve the flow.
    const flow = resolveFlow(form, 'size_key')
    if (flow !== this.state.flow) {
      this.setState({ flow })
    }

    if (!sizeContext.fetching && !sizeContext.sizes?.length) {
      sizeContext.requestSizeList({
        options: {
          limit: 9999,
          sort: ['name'],
        },
      })
    }

    if (!shelterContext.fetching && !shelterContext.shelters?.length) {
      shelterContext.requestShelterList({
        options: {
          limit: 9999,
          sort: ['name'],
        },
        search: {
          _fields: ["id", "uuid", "name"],
          active: true,
          deleted: false,
          organization_integrations: {
            with: {
              _fields: ["id", "uuid", "description", "partner_key", "main", "shelter_id"],
              active: true,
              deleted: false,
            }
          }
        }
      })
    }
  }

  /** Resolve a shelter name for display, from the loaded shelter list. */
  shelterName = (uuid?: string) =>
    this.props.shelterContext.shelters?.find(s => s.uuid === uuid)?.name

  /** Resolve a partner display name. */
  partnerName = (key?: string) =>
    this.props.partnerList?.find(p => p.key === key)?.name || key

  /** Resolve a size name for display, from the loaded size list. */
  sizeName = (key?: string) =>
    this.props.sizeContext.sizes?.find(s => s.key === key)?.name

  // ----- Field groups -----

  renderSizeField = () => {
    const { sizeContext } = this.props
    const sizesOptions = sizeContext.sizes?.map(size => ({
      label: size.name,
      value: size.key,
    }))

    return (
      <Form.Item
        label="Size"
        name="size_key"
        rules={[{ required: true, message: 'Missing a size' }]}
      >
        <Select
          showSearch
          filterOption={DEFAULT_SELECT_FILTER_OPTION}
          loading={sizeContext.fetching}
          placeholder="Select a size"
          options={sizesOptions}
        />
      </Form.Item>
    )
  }

  /** External target (Shelter Buddy animal-size picker, or name + id inputs). */
  renderExternalTarget = () => {
    const { form } = this.props

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) =>
          prev.shelter_uuid !== cur.shelter_uuid ||
          prev.partner_key !== cur.partner_key ||
          prev.integration_uuid !== cur.integration_uuid
        }
      >
        {({ getFieldValue }) => {
          const shelterUuid: string | undefined = getFieldValue("shelter_uuid")
          const partnerKey: string | undefined = getFieldValue("partner_key")
          const integrationUuid: string | undefined = getFieldValue("integration_uuid")

          const canLoadExternal =
            !!shelterUuid && !!integrationUuid && partnerKey === "SHELTER_BUDDY"

          if (canLoadExternal) {
            return (
              <ShelterBuddyExternalField
                form={form}
                integrationUuid={integrationUuid!}
                loadExternalList={loadAnimalSizes}
                label="External size"
                placeholder="Select a size"
              />
            )
          }

          return <ManualExternalInputs />
        }}
      </Form.Item>
    )
  }

  /** Inbound only: read-only display of the incoming external value. */
  renderIncoming = () => (
    <IncomingStep
      form={this.props.form}
      partnerList={this.props.partnerList}
      externalRowLabel="External Size"
    />
  )

  // ----- Review -----

  renderReview = () => {
    const { form } = this.props

    const partnerKey = form.getFieldValue('partner_key')
    const shelterUuid = form.getFieldValue('shelter_uuid')
    const sizeKey = form.getFieldValue('size_key')
    const name = form.getFieldValue('name')
    const externalId = form.getFieldValue('external_id')

    const ourSide = this.sizeName(sizeKey) || '—'
    const externalSide = [name, externalId].filter(Boolean).join(' · ') || '—'
    const mappingSummary = `${ourSide} ↔ ${externalSide}`

    return (
      <>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Direction">
            {DIRECTION_LABEL.BOTH}
          </Descriptions.Item>
          <Descriptions.Item label="Partner">{this.partnerName(partnerKey) || '—'}</Descriptions.Item>
          <Descriptions.Item label="Mapping">{mappingSummary}</Descriptions.Item>
          <Descriptions.Item label="Applies to">
            {shelterUuid ? (this.shelterName(shelterUuid) || shelterUuid) : <Tag>GLOBAL</Tag>}
          </Descriptions.Item>
        </Descriptions>

        <ActiveField />
      </>
    )
  }

  // ----- Step definitions -----

  steps = (): Step[] => {
    const { form, shelterContext } = this.props

    const reviewStep: Step = {
      key: 'review',
      title: 'Review & Save',
      fields: [],
      content: this.renderReview,
    }

    if (this.state.flow === 'inbound') {
      return [
        {
          key: 'incoming',
          title: 'Incoming',
          fields: [],
          content: this.renderIncoming,
        },
        {
          key: 'our-side',
          title: 'Our side',
          fields: ['size_key', 'shelter_uuid'],
          content: () => (
            <>
              {this.renderSizeField()}
              <ScopeField form={form} shelterContext={shelterContext} />
            </>
          ),
        },
        reviewStep,
      ]
    }

    return [
      {
        key: 'our-side',
        title: 'Our side',
        fields: ['size_key', 'partner_key', 'shelter_uuid'],
        content: () => (
          <>
            {this.renderSizeField()}
            <PartnerField partnerList={this.props.partnerList} />
            <ScopeField form={form} shelterContext={shelterContext} />
          </>
        ),
      },
      {
        key: 'external',
        title: 'External target',
        fields: ['name', 'external_id'],
        content: () => (
          <>
            <IntegrationField shelterContext={shelterContext} />
            {this.renderExternalTarget()}
          </>
        ),
      },
      reviewStep,
    ]
  }

  render() {
    return <MappingStepper form={this.props.form} steps={this.steps()} loading={this.props.loading} />
  }
}

export default class MappingSizeForm extends Component<MappingSizeFormProps> {
  render() {
    return (
      <ShelterConsumer>
        {(shelterContext) =>
          <SizeConsumer>
            {(sizeContext) => (
              <MappingSizeFormInner
                form={this.props.form}
                partnerList={this.props.partnerList}
                loading={this.props.loading}
                shelterContext={shelterContext}
                sizeContext={sizeContext}
              />
            )}
          </SizeConsumer>
        }
      </ShelterConsumer>
    )
  }
}
